import { Check, ChevronDown } from 'lucide-react'
import type { ComponentProps } from 'react'

import SymbolLink from '@/components/ui/SymbolLink'
import { TOUCH_TARGET_Y } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

/** A dot is a mark, so it takes the fill grade, not the text grade a figure uses. */
const DOT_TONES = {
  up: 'bg-market-up-fill',
  down: 'bg-market-down-fill',
  accent: 'bg-accent',
  muted: 'bg-muted-foreground',
} as const

/**
 * Every chip is square (the 2026-09-28 design refresh draws radius 0 everywhere), and its hover
 * also answers `:focus-visible`, so a keyboard reaches the same state a pointer does.
 *
 * `boxed` is the lens-on-one-dataset tab (Market › Movers `4236:104328`): a `bg-muted` box with a
 * `border-border` hairline whose selected state lifts to `bg-popover` and `border-border-strong`
 * with a 600 label. Hovering an unselected one strengthens the border and the label and leaves
 * the fill alone (`4243:104918`); the selected one does not change on hover.
 *
 * `subtab` (sz:kit) is the stock page's Advanced sub-tab chip (mock `ctl-vchip`, owner ruling C7):
 * `boxed`'s box on the quieter subtle hairline, which hover lifts one step to the default hairline
 * as the label goes white; the selected one sits on the raised fill with the strong hairline and a
 * 600 label, and does not change on hover.
 */
const SKINS = {
  filter: {
    on: 'border-border-subtle bg-popover text-foreground',
    off: 'border-transparent text-muted-foreground hover:text-foreground focus-visible:text-foreground',
  },
  boxed: {
    on: 'border-border-strong bg-popover text-h-s text-foreground',
    off: 'border-border bg-muted text-muted-foreground hover:border-border-strong hover:text-foreground focus-visible:border-border-strong focus-visible:text-foreground',
  },
  subtab: {
    on: 'border-border-strong bg-popover text-h-s text-foreground',
    off: 'border-border-subtle bg-muted text-muted-foreground hover:border-border hover:text-foreground focus-visible:border-border focus-visible:text-foreground',
  },
} as const

/** The skins that draw a box: both share its sizes and its bold-label reserve. */
const isBoxed = (skin: keyof typeof SKINS) => skin !== 'filter'

/** `boxed`'s two boxes: 36px, and the compact 32px (`4236:70217`). */
const BOXED_SIZES = {
  md: 'h-9 px-4 text-body-m',
  sm: 'h-8 px-3 text-body-m',
} as const

/**
 * The box every button chip starts from: 28px tall, which is under the 44px touch minimum, so
 * below `lg` the hit area grows VERTICALLY past the visual box to 44px (`TOUCH_TARGET_Y`). Vertically only: a chip is already wider than
 * 44px, and extending sideways too would make neighbouring chips in a filter row overlap each
 * other's targets. Growing the box itself is not an option — chips sit inline with 40px search
 * fields and 36px tabs, and a 44px chip would reflow every one of those rows at the width with
 * least room.
 *
 * The border is present in every state — transparent where nothing is drawn — so a state change
 * never shifts the row by two pixels.
 *
 * sz:kit (owner rulings A1, A2): a press scales the chip to .97 where motion is welcome (the mock's
 * `transform: scale(.97)`), and focus is the white control ring.
 */
const CHIP =
  'relative inline-flex h-7 shrink-0 cursor-pointer items-center gap-2 border px-4 text-label-m whitespace-nowrap transition-[color,background-color,border-color,scale] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control motion-safe:active:scale-97 disabled:pointer-events-none disabled:opacity-38'
const CHIP_HIT_AREA = TOUCH_TARGET_Y

interface ChipProps extends ComponentProps<'button'> {
  /** Filled, hairlined state. Selection is the caller's to own. */
  active?: boolean
  /** Decorative status dot before the label — "● LIVE", a sector's direction. */
  dot?: keyof typeof DOT_TONES
  /** Trailing caret for the chips that open a menu ("Sector ▾"). */
  chevron?: boolean
  /**
   * `filter` is the 28px chip. `boxed` is the lens-on-one-dataset tab, 36px (32px with
   * `size="sm"`). `subtab` is the stock page's Advanced sub-tab chip, `boxed`'s box on the subtle
   * hairline. `tab` is the pre-refresh name for `boxed`, kept so existing callers compile;
   * it renders exactly as `boxed` does, and new code should say `boxed`.
   */
  variant?: keyof typeof SKINS | 'tab'
  /** `boxed` and `subtab` only — `filter` never reads it. See `BOXED_SIZES`. */
  size?: keyof typeof BOXED_SIZES
}

/**
 * A real `<button>`, because every chip in the frames does something: picks a timeframe, opens a
 * filter menu, narrows a list.
 */
export default function Chip({
  active = false,
  dot,
  chevron = false,
  variant = 'filter',
  size = 'md',
  className,
  children,
  ...props
}: ChipProps) {
  const skin = variant === 'tab' ? 'boxed' : variant

  return (
    <button
      type="button"
      className={cn(
        CHIP,
        CHIP_HIT_AREA,
        isBoxed(skin) ? BOXED_SIZES[size] : null,
        SKINS[skin][active ? 'on' : 'off'],
        className,
      )}
      {...props}
    >
      {dot ? (
        <span className={cn('size-2 shrink-0 rounded-full', DOT_TONES[dot])} aria-hidden="true" />
      ) : null}
      {isBoxed(skin) && typeof children === 'string' ? (
        // The selected label is 600 and wider than the 400 it was; the reserve keeps the box
        // as wide as the bold one, so choosing a tab never moves its neighbours.
        <span data-text={children} className="reserve-bold">
          {children}
        </span>
      ) : (
        children
      )}
      {chevron ? <ChevronDown className="size-4 shrink-0" aria-hidden="true" /> : null}
    </button>
  )
}

interface ToggleChipProps extends Omit<ComponentProps<'button'>, 'aria-pressed'> {
  /**
   * `chip` is the pill. `segment` is one item of a multi-select that sits beside a
   * `SegmentedControl size="lg"`: same 36px box, padding and lifted look, so the two read as one
   * family. Put the items in a `bg-muted p-1` track.
   *
   * `layer` (sz:kit) is the Signal chart's layer switch (mock `sig-toggle`): 32px, 14/400, the
   * inset fill with no edge, lifting to the raised fill on hover (A1); on, the accent fill, which
   * hover leaves alone. No ✓: the accent fill against the inset grey is the state, as the mock
   * draws it, and `aria-pressed` says it to assistive technology.
   */
  variant?: 'chip' | 'segment' | 'layer'
  /** On or off. The chip owns `aria-pressed` and the ✓, so neither can drift from this. */
  pressed: boolean
}

/**
 * One switch in a multi-select row — "Notify me by: ✓ App ✓ Email SMS" (`4243:89064`).
 *
 * `aria-pressed` rather than a checkbox: each chip is a single on/off control that reads as a
 * button, and pressed is what a screen reader announces for exactly that. The ✓ repeats the
 * state for the eye, so on and off never differ by colour alone, and is hidden from assistive
 * technology because the pressed state already says it.
 */
export function ToggleChip({
  pressed,
  variant = 'chip',
  className,
  children,
  ...props
}: ToggleChipProps) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cn(
        CHIP,
        CHIP_HIT_AREA,
        variant === 'layer'
          ? [
              'h-8 px-4 text-body-m',
              pressed
                ? 'border-transparent bg-accent text-accent-foreground'
                : 'border-transparent bg-muted text-foreground hover:bg-popover focus-visible:bg-popover',
            ]
          : variant === 'segment'
            ? [
                'h-9 justify-center px-4 text-body-m',
                pressed
                  ? 'border-border-strong bg-popover text-h-s text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground focus-visible:text-foreground',
              ]
            : [
                // 14, not the filter chip's 12: these are a form's answers, set at the size of the
                // labels and fields around them.
                'px-3 text-body-m',
                pressed
                  ? 'border-transparent bg-tone-accent-soft text-accent-soft-foreground hover:border-border-accent focus-visible:border-border-accent'
                  : 'border-transparent bg-muted text-muted-foreground hover:border-border-strong hover:text-foreground focus-visible:border-border-strong focus-visible:text-foreground',
              ],
        className,
      )}
      {...props}
    >
      {pressed && variant !== 'layer' ? (
        <Check className="size-3 shrink-0" aria-hidden="true" />
      ) : null}
      {children}
    </button>
  )
}

interface SymbolChipProps {
  /** The listed ticker, e.g. `NABIL`: both the label and the stock page it opens. */
  symbol: string
  /** Forwarded to the link: the company name, a figure — what the ticker alone does not say. */
  title?: string | undefined
  className?: string | undefined
}

/**
 * A ticker tag that opens its stock — news tags, "people also look at" (`4243:93645`).
 *
 * A link, not a button: it navigates, so it keeps middle-click, cmd-click and the URL preview,
 * and SymbolLink already stops the click reaching a card or row that has its own `onClick`. The
 * hover fills with `bg-popover` and brightens the label instead of underlining, as the frame's
 * chip row draws it, with no edge. 24px tall, so below `lg` the target grows to 44px.
 */
export function SymbolChip({ symbol, title, className }: SymbolChipProps) {
  return (
    <SymbolLink
      symbol={symbol}
      title={title}
      underline="group"
      className={cn(
        'relative inline-flex h-6 shrink-0 items-center border border-transparent bg-muted px-2 text-h-xs whitespace-nowrap text-muted-foreground transition-colors',
        'hover:bg-popover hover:text-foreground focus-visible:bg-popover focus-visible:text-foreground',
        TOUCH_TARGET_Y,
        className,
      )}
    />
  )
}
